import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { webhookEventsAPI } from '../utils/api';
import { WebhookEvent } from '../types';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';

interface WebhookEventsFilters {
  page: number;
  limit: number;
  channel?: string;
  processing_status?: string;
  date_from?: string;
  date_to?: string;
}

const WebhookEventsTab: React.FC = () => {
  const [filters, setFilters] = useState<WebhookEventsFilters>({
    page: 1,
    limit: 20,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['webhookEvents', filters],
    queryFn: async () => {
      const response = await webhookEventsAPI.getEvents(filters);
      return response.data;
    },
  });

  const events: WebhookEvent[] = eventsData?.data || [];
  const pagination = eventsData?.pagination;

  const handleFilterChange = (key: keyof WebhookEventsFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const hasActiveFilters = filters.channel || filters.processing_status || filters.date_from || filters.date_to;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const getChannelBadge = (channel: string) => {
    if (channel === 'telegram') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
          Telegram
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <DevicePhoneMobileIcon className="h-3 w-3 mr-1" />
        WhatsApp
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      RECEIVED: { color: 'bg-yellow-100 text-yellow-800', label: 'Ricevuto' },
      PROCESSED: { color: 'bg-green-100 text-green-800', label: 'Elaborato' },
      FAILED: { color: 'bg-red-100 text-red-800', label: 'Fallito' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Channel filter */}
          <select
            value={filters.channel || ''}
            onChange={(e) => handleFilterChange('channel', e.target.value || undefined)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tutti i canali</option>
            <option value="telegram">Telegram</option>
            <option value="whatsapp">WhatsApp</option>
          </select>

          {/* Status filter */}
          <select
            value={filters.processing_status || ''}
            onChange={(e) => handleFilterChange('processing_status', e.target.value || undefined)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="RECEIVED">Ricevuto</option>
            <option value="PROCESSED">Elaborato</option>
            <option value="FAILED">Fallito</option>
          </select>

          {/* Date from */}
          <input
            type="date"
            value={filters.date_from?.split('T')[0] || ''}
            onChange={(e) =>
              handleFilterChange('date_from', e.target.value ? `${e.target.value}T00:00:00` : undefined)
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            title="Data da"
          />

          {/* Date to */}
          <input
            type="date"
            value={filters.date_to?.split('T')[0] || ''}
            onChange={(e) =>
              handleFilterChange('date_to', e.target.value ? `${e.target.value}T23:59:59` : undefined)
            }
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            title="Data a"
          />

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Cancella filtri</span>
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data/Ora
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Canale
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IP
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Errore
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                  <p className="mt-2 text-gray-500">Caricamento...</p>
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <p className="mt-2 text-gray-500">Nessun evento webhook trovato</p>
                </td>
              </tr>
            ) : (
              events.map((evt) => (
                <React.Fragment key={evt.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => toggleExpand(evt.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {expandedId === evt.id ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span>{formatDate(evt.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getChannelBadge(evt.channel)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(evt.processing_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {evt.source_ip || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600 max-w-xs truncate">
                      {evt.error_message || '-'}
                    </td>
                  </tr>
                  {expandedId === evt.id && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 bg-gray-50">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase">Raw Payload</label>
                          <pre className="mt-1 p-3 bg-white border border-gray-200 rounded-lg text-xs text-gray-900 overflow-x-auto max-h-96 overflow-y-auto">
                            {JSON.stringify(evt.raw_payload, null, 2)}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} di{' '}
            {pagination.total} risultati
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleFilterChange('page', pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages}
            </span>
            <button
              onClick={() => handleFilterChange('page', pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookEventsTab;
